// src/pages/rh/Contrats.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
    Card, Table, Row, Col, Form, Button, Pagination,
    Modal, Badge, Spinner, Tooltip, OverlayTrigger
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import Footer from "../../layouts/Footer";
import Header from "../../layouts/Header";
import { formatDate } from "../../utils/formatters";
import { API_BASE_URL } from "../../constants/api";
import { COLORS } from "../../constants/colors";

/* ============================================================
   HELPERS DE COMPARAISON DE DATES (sans heure)
   ============================================================ */
const getDateOnly = (dateInput) => {
    if (!dateInput) return null;
    const d = new Date(dateInput);
    if (isNaN(d.getTime())) return null;
    d.setHours(0, 0, 0, 0);
    return d;
};

const isBeforeToday = (dateInput) => {
    const d = getDateOnly(dateInput);
    if (!d) return false;
    const today = getDateOnly(new Date());
    return d < today;
};

const isAfterToday = (dateInput) => {
    const d = getDateOnly(dateInput);
    if (!d) return false;
    const today = getDateOnly(new Date());
    return d > today;
};

const isToday = (dateInput) => {
    const d = getDateOnly(dateInput);
    if (!d) return false;
    const today = getDateOnly(new Date());
    return d.getTime() === today.getTime();
};

const isWithinNext7Days = (dateInput) => {
    const d = getDateOnly(dateInput);
    if (!d) return false;
    const today = getDateOnly(new Date());
    const weekLater = getDateOnly(new Date());
    weekLater.setDate(weekLater.getDate() + 7);
    return d >= today && d <= weekLater;
};

/* ============================================================
   TOAST NOTIFICATION — Design charte
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 5000 }) => {
    if (!show) return null;
    const config = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bgColor: COLORS.info || '#3b82f6', icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function Contrats() {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [contratToDelete, setContratToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [toast, setToast] = useState({ show: false, type: '', title: '', message: '' });
    const [stats, setStats] = useState(null);

    useEffect(() => {
        fetchContrats();
        fetchStats();
    }, []);

    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: '', title: '', message: '' });

    /* ============================================================
       FETCH
       ============================================================ */
    const fetchContrats = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/contrats.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) {
                if (response.status === 401) {
                    localStorage.removeItem("token");
                    localStorage.removeItem("user");
                    navigate("/pages/signin2");
                    throw new Error("Session expirée");
                }
                throw new Error(`Erreur HTTP: ${response.status}`);
            }
            const result = await response.json();
            setData(result.data || []);
        } catch (err) {
            setError(err.message);
            showToast('error', 'Erreur', err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/contrats.php?action=stats`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur chargement stats", err);
        }
    };

    /* ============================================================
       ACTIONS
       ============================================================ */
    const handleDeleteClick = (contrat) => {
        setContratToDelete(contrat);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (!contratToDelete) return;
        setDeleting(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/contrats.php?id=${contratToDelete.id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const result = await response.json();
            if (result.success) {
                setShowDeleteModal(false);
                setContratToDelete(null);
                fetchContrats();
                fetchStats();
                showToast('success', 'Succès', "Contrat supprimé avec succès");
            } else {
                showToast('error', 'Erreur', result.message || "Erreur lors de la suppression");
                setShowDeleteModal(false);
            }
        } catch (err) {
            showToast('error', 'Erreur', "Erreur de connexion au serveur");
            setShowDeleteModal(false);
        } finally {
            setDeleting(false);
        }
    };

    /* ============================================================
       HELPERS DE STATUT — CORRIGÉS
       ============================================================ */
    const getStatutConfig = (contrat) => {
        // 1️⃣ Statut forcé en BDD
        if (contrat.statut === 'termine') {
            return { gradient: 'linear-gradient(135deg, #64748b, #475569)', text: 'Terminé', icon: 'ri-checkbox-circle-line' };
        }
        if (contrat.statut === 'expire') {
            return { gradient: 'linear-gradient(135deg, #ef4444, #dc2626)', text: 'Expiré', icon: 'ri-error-warning-line' };
        }

        // 2️⃣ Comparaison basée sur les DATES uniquement (sans heure)
        if (contrat.date_fin && isBeforeToday(contrat.date_fin)) {
            return { gradient: 'linear-gradient(135deg, #ef4444, #dc2626)', text: 'Expiré', icon: 'ri-error-warning-line' };
        }
        if (contrat.date_debut && isAfterToday(contrat.date_debut)) {
            return { gradient: 'linear-gradient(135deg, #f59e0b, #d97706)', text: 'À venir', icon: 'ri-time-line' };
        }
        return { gradient: 'linear-gradient(135deg, #10b981, #059669)', text: 'Actif', icon: 'ri-checkbox-circle-line' };
    };

    const getStatutBadge = (contrat) => {
        const config = getStatutConfig(contrat);
        return (
            <span className="status-badge-charte" style={{ background: config.gradient }}>
                <i className={config.icon}></i>
                {config.text}
            </span>
        );
    };

    const getTypeContratConfig = (contrat) => {
        // Utiliser le type en BDD en priorité
        if (contrat.type_contrat) {
            const types = {
                'CDD':       { gradient: 'linear-gradient(135deg, #06b6d4, #0891b2)', label: 'CDD',        icon: 'ri-file-chart-line' },
                'CDI':       { gradient: 'linear-gradient(135deg, #3b82f6, #2563eb)', label: 'CDI',        icon: 'ri-file-text-line' },
                'AVENANT':   { gradient: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', label: 'Avenant',    icon: 'ri-file-add-line' },
                'CERTIFICAT':{ gradient: 'linear-gradient(135deg, #f59e0b, #d97706)', label: 'Certificat', icon: 'ri-award-line' }
            };
            return types[contrat.type_contrat] || types['CDD'];
        }
        // Fallback
        if (contrat.date_fin) {
            return { gradient: 'linear-gradient(135deg, #06b6d4, #0891b2)', label: 'CDD', icon: 'ri-file-chart-line' };
        }
        return { gradient: 'linear-gradient(135deg, #3b82f6, #2563eb)', label: 'CDI', icon: 'ri-file-text-line' };
    };

    const getTypeContratBadge = (contrat) => {
        const config = getTypeContratConfig(contrat);
        return (
            <span className="status-badge-charte" style={{ background: config.gradient }}>
                <i className={config.icon}></i>
                {config.label}
            </span>
        );
    };

    /* ============================================================
       FILTER / PAGINATION
       ============================================================ */
    const filteredItems = useMemo(() => {
        let filtered = data.filter(item => {
            const textMatch =
                (item.employe_nom || '').toLowerCase().includes(filterText.toLowerCase()) ||
                (item.employe_prenom || '').toLowerCase().includes(filterText.toLowerCase()) ||
                (item.employeur_nom || '').toLowerCase().includes(filterText.toLowerCase());
            return textMatch;
        });
        filtered.sort((a, b) => new Date(b.date_debut) - new Date(a.date_debut));
        return filtered;
    }, [filterText, data]);

    const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentItems = filteredItems.slice(startIndex, startIndex + itemsPerPage);

    // ⭐ Utilise la nouvelle fonction isWithinNext7Days (sans heure)
    const expiringSoonCount = filteredItems.filter(c => {
        return c.statut === 'actif'
            && c.date_fin
            && !isBeforeToday(c.date_fin)
            && isWithinNext7Days(c.date_fin);
    }).length;

    const ActionButton = ({ icon, onClick, tooltip, color }) => (
        <OverlayTrigger placement="top" overlay={<Tooltip>{tooltip}</Tooltip>}>
            <button
                className="action-btn-charte"
                onClick={onClick}
                style={{ '--btn-color': color || COLORS.primary }}
            >
                <i className={icon}></i>
            </button>
        </OverlayTrigger>
    );

    /* ============================================================
       LOADING
       ============================================================ */
    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center" style={{ maxWidth: '400px' }}>
                            <div className="loading-ring-wrapper mb-4">
                                <div className="loading-ring"></div>
                                <div className="loading-ring-inner"></div>
                                <i className="ri-loader-4-fill loading-icon"></i>
                            </div>
                            <h5 className="fw-bold" style={{ color: COLORS.primaryDark, letterSpacing: '0.3px' }}>
                                Chargement des contrats...
                            </h5>
                            <p className="text-muted small mb-0">Veuillez patienter quelques instants...</p>
                            <div className="mx-auto mt-4" style={{ width: '80%' }}>
                                <div className="progress" style={{ height: '3px', background: '#e9ecef', borderRadius: '10px', overflow: 'hidden' }}>
                                    <div
                                        className="progress-bar progress-bar-striped progress-bar-animated"
                                        style={{
                                            width: '100%',
                                            background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary}, ${COLORS.primary})`,
                                            backgroundSize: '200% 100%',
                                            animation: 'shimmer 1.5s linear infinite'
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
                <style>{`
                    .loading-ring-wrapper { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                    .loading-ring { position: absolute; top: 0; left: 0; width: 100%; height: 100%; border-radius: 50%; border: 4px solid rgba(33,172,205,0.15); border-top-color: ${COLORS.primary}; animation: spin 1s linear infinite; }
                    .loading-ring-inner { position: absolute; top: 8px; left: 8px; right: 8px; bottom: 8px; border-radius: 50%; border: 3px solid rgba(33,172,205,0.08); border-bottom-color: ${COLORS.secondary}; animation: spin 0.8s linear infinite reverse; }
                    .loading-icon { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 28px; color: ${COLORS.primary}; animation: pulse-icon 1.2s ease-in-out infinite; }
                    @keyframes spin { to { transform: rotate(360deg); } }
                    @keyframes pulse-icon { 0%,100% { transform: translate(-50%,-50%) scale(1); opacity:1; } 50% { transform: translate(-50%,-50%) scale(1.15); opacity:0.6; } }
                    @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
                `}</style>
            </>
        );
    }

    /* ============================================================
       RENDU PRINCIPAL
       ============================================================ */
    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>

                <ToastNotification
                    show={toast.show}
                    onClose={hideToast}
                    type={toast.type}
                    title={toast.title}
                    message={toast.message}
                />

                <div className="container-fluid">

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-file-copy-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">Gestion des contrats</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-bar-chart-line"></i> Total {stats?.total || 0}
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.success}15`, color: COLORS.success }}>
                                        <i className="ri-checkbox-circle-line"></i> Actifs {stats?.actifs || 0}
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.info}15`, color: COLORS.info }}>
                                        <i className="ri-file-text-line"></i> CDI {stats?.cdi || 0}
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.danger}15`, color: COLORS.danger }}>
                                        <i className="ri-error-warning-line"></i> Expirés {stats?.expires || 0}
                                    </span>
                                    {expiringSoonCount > 0 && (
                                        <span className="badge-modern" style={{ background: `${COLORS.warning}15`, color: COLORS.warning }}>
                                            <i className="ri-alert-line"></i> Expirent bientôt {expiringSoonCount}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button className="btn-modern-outline" onClick={fetchContrats} disabled={loading}>
                                <i className={`ri-refresh-line ${loading ? 'spin-icon' : ''}`}></i> Actualiser
                            </Button>
                            <Button className="btn-modern-primary" as={Link} to="/rh/contratsNouveau">
                                <i className="ri-add-line"></i> Nouveau contrat
                            </Button>
                            <Button className="btn-modern-warning" as={Link} to="/rh/contratsAlertes">
                                <i className="ri-alert-line me-1"></i> Alertes
                                {expiringSoonCount > 0 && (
                                    <span className="badge-count-alert-charte">{expiringSoonCount}</span>
                                )}
                            </Button>
                        </div>
                    </div>

                    {/* ===== KPI CARDS ===== */}
                    {stats && (
                        <Row className="g-3 mb-4">
                            <Col sm="6" xl="3">
                                <div className="kpi-charte kpi-primary">
                                    <div className="kpi-icon-charte"><i className="ri-file-copy-line"></i></div>
                                    <div className="kpi-content-charte">
                                        <span className="kpi-label-charte">Total contrats</span>
                                        <h3 className="kpi-value-charte">{stats.total || 0}</h3>
                                    </div>
                                </div>
                            </Col>
                            <Col sm="6" xl="3">
                                <div className="kpi-charte kpi-success">
                                    <div className="kpi-icon-charte"><i className="ri-checkbox-circle-line"></i></div>
                                    <div className="kpi-content-charte">
                                        <span className="kpi-label-charte">Contrats actifs</span>
                                        <h3 className="kpi-value-charte">{stats.actifs || 0}</h3>
                                    </div>
                                </div>
                            </Col>
                            <Col sm="6" xl="3">
                                <div className="kpi-charte kpi-info">
                                    <div className="kpi-icon-charte"><i className="ri-file-text-line"></i></div>
                                    <div className="kpi-content-charte">
                                        <span className="kpi-label-charte">CDI</span>
                                        <h3 className="kpi-value-charte">{stats.cdi || 0}</h3>
                                    </div>
                                </div>
                            </Col>
                            <Col sm="6" xl="3">
                                <div className="kpi-charte kpi-danger">
                                    <div className="kpi-icon-charte"><i className="ri-error-warning-line"></i></div>
                                    <div className="kpi-content-charte">
                                        <span className="kpi-label-charte">Expirés</span>
                                        <h3 className="kpi-value-charte">{stats.expires || 0}</h3>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    )}

                    {/* ===== FILTRES ===== */}
                    <Card className="filter-card-charte mb-4">
                        <Card.Body className="py-3 px-4">
                            <Row className="align-items-center g-3">
                                <Col md="5">
                                    <div className="search-box-charte">
                                        <i className="ri-search-line search-icon-charte"></i>
                                        <Form.Control
                                            type="text"
                                            placeholder="Rechercher par employé ou employeur..."
                                            value={filterText}
                                            onChange={e => setFilterText(e.target.value)}
                                            className="search-input-charte"
                                        />
                                    </div>
                                </Col>
                                <Col md="4">
                                    <Form.Select
                                        value={itemsPerPage}
                                        onChange={(e) => setItemsPerPage(Number(e.target.value))}
                                        className="filter-select-charte"
                                    >
                                        <option value={5}>5 par page</option>
                                        <option value={10}>10 par page</option>
                                        <option value={25}>25 par page</option>
                                        <option value={50}>50 par page</option>
                                    </Form.Select>
                                </Col>
                                <Col md="3" className="text-end">
                                    <span className="count-badge-charte">
                                        <i className="ri-stack-line"></i>
                                        {filteredItems.length} contrat(s)
                                    </span>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* ===== TABLEAU ===== */}
                    <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                        <Card.Header className="card-header-gradient">
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <div className="d-flex align-items-center gap-2">
                                    <i className="ri-file-copy-line text-white fs-5"></i>
                                    <span className="fw-semibold text-white">Liste des contrats</span>
                                    <span className="badge-header-light">{filteredItems.length}</span>
                                </div>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0 table-charte">
                                    <thead>
                                        <tr>
                                            <th className="ps-4">Employé</th>
                                            <th>Employeur</th>
                                            <th>Date début</th>
                                            <th>Date fin</th>
                                            <th className="text-center">Type</th>
                                            <th className="text-center">Statut</th>
                                            <th className="text-center pe-4">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.length > 0 ? currentItems.map((contrat) => {
                                            // ⭐ Calcul SANS heure pour éviter le bug "Expiré aujourd'hui"
                                            const isExpiringSoon = contrat.statut === 'actif'
                                                && contrat.date_fin
                                                && !isBeforeToday(contrat.date_fin)
                                                && isWithinNext7Days(contrat.date_fin);

                                            const isExpiringToday = contrat.statut === 'actif'
                                                && contrat.date_fin
                                                && isToday(contrat.date_fin);

                                            return (
                                                <tr key={contrat.id} className={isExpiringSoon || isExpiringToday ? 'row-late-charte' : ''}>
                                                    <td className="ps-4">
                                                        <div className="d-flex align-items-center gap-3">
                                                            <div className="employee-avatar-charte">
                                                                {contrat.employe_prenom?.charAt(0)}{contrat.employe_nom?.charAt(0)}
                                                            </div>
                                                            <div>
                                                                <div className="fw-semibold" style={{ fontSize: '0.85rem' }}>
                                                                    {contrat.employe_prenom} {contrat.employe_nom}
                                                                </div>
                                                                <small className="text-muted">
                                                                    <i className="ri-mail-line me-1" style={{ fontSize: '0.7rem' }}></i>
                                                                    {contrat.employe_email || '—'}
                                                                </small>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="fw-semibold" style={{ fontSize: '0.85rem' }}>{contrat.employeur_nom}</div>
                                                        <small className="text-muted">{contrat.employeur_email || '—'}</small>
                                                    </td>
                                                    <td className="text-muted">{formatDate(contrat.date_debut)}</td>
                                                    <td>
                                                        <span className={isExpiringSoon || isExpiringToday ? 'text-warning fw-semibold' : 'text-muted'}>
                                                            {contrat.date_fin ? formatDate(contrat.date_fin) : 'CDI'}
                                                        </span>
                                                        {isExpiringToday && (
                                                            <span className="badge-late-charte">
                                                                <i className="ri-alarm-warning-line"></i>Dernier jour
                                                            </span>
                                                        )}
                                                        {isExpiringSoon && !isExpiringToday && (
                                                            <span className="badge-late-charte">
                                                                <i className="ri-alert-line"></i>Expire bientôt
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="text-center">{getTypeContratBadge(contrat)}</td>
                                                    <td className="text-center">{getStatutBadge(contrat)}</td>
                                                    <td className="text-center pe-4">
                                                        <div className="d-flex gap-2 justify-content-center">
                                                            <ActionButton
                                                                icon="ri-eye-line"
                                                                onClick={() => navigate(`/rh/contrats/${contrat.id}`)}
                                                                tooltip="Voir détails"
                                                            />
                                                            <ActionButton
                                                                icon="ri-delete-bin-line"
                                                                onClick={() => handleDeleteClick(contrat)}
                                                                tooltip="Supprimer"
                                                                color={COLORS.danger}
                                                            />
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        }) : (
                                            <tr>
                                                <td colSpan="7">
                                                    <div className="empty-state-charte">
                                                        <div className="empty-state-icon-charte">
                                                            <i className="ri-inbox-line"></i>
                                                        </div>
                                                        <h6 className="fw-semibold mb-1">Aucun contrat trouvé</h6>
                                                        <p className="text-muted small mb-0">Cliquez sur "Nouveau contrat" pour en créer un</p>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>

                    {/* ===== PAGINATION ===== */}
                    {totalPages > 1 && !loading && (
                        <div className="pagination-wrapper-charte mt-3">
                            <div className="pagination-info-charte">
                                Affichage de <strong>{startIndex + 1}</strong> à <strong>{Math.min(startIndex + itemsPerPage, filteredItems.length)}</strong> sur <strong>{filteredItems.length}</strong> contrats
                            </div>
                            <Pagination className="pagination-charte mb-0">
                                <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} />
                                {[...Array(Math.min(5, totalPages))].map((_, i) => {
                                    let pageNum;
                                    if (totalPages <= 5) pageNum = i + 1;
                                    else if (currentPage <= 3) pageNum = i + 1;
                                    else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                                    else pageNum = currentPage - 2 + i;
                                    return (
                                        <Pagination.Item key={i} active={pageNum === currentPage} onClick={() => setCurrentPage(pageNum)}>
                                            {pageNum}
                                        </Pagination.Item>
                                    );
                                })}
                                <Pagination.Next onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} />
                            </Pagination>
                        </div>
                    )}
                </div>
                <Footer />
            </div>

            {/* ===== MODAL SUPPRESSION ===== */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="modal-charte">
                <Modal.Header closeButton className="modal-header-charte" style={{ background: `linear-gradient(135deg, ${COLORS.danger}, #dc2626)` }}>
                    <div className="d-flex align-items-center gap-3">
                        <div className="modal-header-icon"><i className="ri-delete-bin-line"></i></div>
                        <div>
                            <Modal.Title className="fs-5 fw-bold text-white mb-0">Confirmer la suppression</Modal.Title>
                            <p className="text-white-50 mb-0 small">Cette action est irréversible</p>
                        </div>
                    </div>
                </Modal.Header>

                <Modal.Body className="p-4">
                    {contratToDelete && (
                        <>
                            <p className="text-center text-muted mb-3">
                                Êtes-vous sûr de vouloir supprimer définitivement ce contrat ?
                            </p>

                            <div className="summary-card-charte mb-3">
                                <div className="d-flex align-items-center gap-3">
                                    <div className="employe-avatar-charte">
                                        {contratToDelete.employe_prenom?.charAt(0)}{contratToDelete.employe_nom?.charAt(0)}
                                    </div>
                                    <div>
                                        <div className="fw-bold" style={{ color: COLORS.primaryDark }}>
                                            {contratToDelete.employe_prenom} {contratToDelete.employe_nom}
                                        </div>
                                        <div className="text-muted small">
                                            <i className="ri-building-line me-1"></i>
                                            {contratToDelete.employeur_nom}
                                        </div>
                                        <div className="text-muted small">
                                            <i className="ri-calendar-line me-1"></i>
                                            Du {formatDate(contratToDelete.date_debut)} au {contratToDelete.date_fin ? formatDate(contratToDelete.date_fin) : 'CDI'}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="alert-error-charte mb-0">
                                <div className="d-flex align-items-center">
                                    <div className="alert-error-icon"><i className="ri-alert-line"></i></div>
                                    <div className="ms-3">
                                        <strong>Attention</strong>
                                        <p className="mb-0 small">Toutes les données associées seront définitivement supprimées</p>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </Modal.Body>

                <Modal.Footer className="border-0 pb-4 px-4 pt-0">
                    <Button onClick={() => setShowDeleteModal(false)} className="btn-modern-outline" disabled={deleting}>
                        Annuler
                    </Button>
                    <Button onClick={handleConfirmDelete} disabled={deleting} className="btn-modern-danger">
                        {deleting ? (
                            <><Spinner size="sm" className="me-2" />Suppression...</>
                        ) : (
                            <><i className="ri-delete-bin-line me-2"></i>Oui, supprimer</>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }
                .spin-icon { animation: spin 1s linear infinite; }
                .badge-count-alert-charte {
                    background: white; color: var(--danger);
                    border-radius: 50px; padding: 0.1rem 0.5rem;
                    font-size: 0.65rem; font-weight: 700;
                    margin-left: 6px;
                }

                /* ===== KPI ===== */
                .kpi-charte {
                    background: white; border-radius: 12px;
                    padding: 1.2rem;
                    display: flex; align-items: center; gap: 1rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid;
                    transition: all 0.25s ease;
                    height: 100%;
                }
                .kpi-charte:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 12px 28px rgba(0,0,0,0.08);
                }
                .kpi-primary { border-left-color: var(--primary); }
                .kpi-success { border-left-color: var(--success); }
                .kpi-danger  { border-left-color: var(--danger); }
                .kpi-info    { border-left-color: var(--info); }
                .kpi-icon-charte {
                    width: 48px; height: 48px; border-radius: 12px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.4rem; flex-shrink: 0;
                }
                .kpi-primary .kpi-icon-charte { background: ${COLORS.primary}15; color: var(--primary); }
                .kpi-success .kpi-icon-charte { background: ${COLORS.success}15; color: var(--success); }
                .kpi-danger  .kpi-icon-charte { background: ${COLORS.danger}15; color: var(--danger); }
                .kpi-info    .kpi-icon-charte { background: ${COLORS.info}15; color: var(--info); }
                .kpi-content-charte { flex: 1; min-width: 0; }
                .kpi-label-charte {
                    font-size: 0.7rem; color: #94a3b8;
                    text-transform: uppercase; font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value-charte {
                    font-size: 1.35rem; font-weight: 700;
                    margin: 0.3rem 0; color: #1e293b;
                }

                /* ===== FILTRES ===== */
                .filter-card-charte {
                    background: linear-gradient(135deg, var(--primary-dark), var(--primary));
                    border-radius: 12px; border: none;
                    box-shadow: 0 8px 24px rgba(41,90,167,0.15);
                }
                .search-box-charte { position: relative; }
                .search-icon-charte {
                    position: absolute; top: 50%; left: 1rem;
                    transform: translateY(-50%);
                    color: var(--primary); font-size: 1rem;
                    z-index: 2; pointer-events: none;
                }
                .search-input-charte {
                    background: white !important;
                    border: none !important;
                    border-radius: 50px !important;
                    padding: 0.6rem 1rem 0.6rem 2.75rem !important;
                    font-size: 0.9rem;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                }
                .search-input-charte:focus { box-shadow: 0 0 0 3px rgba(255,255,255,0.2) !important; }
                .filter-select-charte {
                    background: white !important;
                    border: none !important;
                    border-radius: 50px !important;
                    padding: 0.6rem 1rem !important;
                    font-size: 0.9rem;
                    cursor: pointer;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                }
                .filter-select-charte:focus { box-shadow: 0 0 0 3px rgba(255,255,255,0.2) !important; }
                .count-badge-charte {
                    background: rgba(255,255,255,0.95); color: var(--primary);
                    padding: 0.6rem 1.25rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                }

                /* ===== HEADER GRADIENT ===== */
                .card-header-gradient {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }
                .badge-header-light {
                    background: rgba(255,255,255,0.2);
                    color: white;
                    border: 1px solid rgba(255,255,255,0.3);
                    border-radius: 50px;
                    padding: 0.25rem 0.75rem;
                    font-size: 0.75rem;
                    font-weight: 600;
                }

                /* ===== TABLE ===== */
                .table-charte thead th {
                    background: #f8fafc; color: #475569;
                    font-weight: 600; font-size: 0.7rem;
                    text-transform: uppercase; letter-spacing: 0.5px;
                    border-bottom: 2px solid #e2e8f0;
                    padding: 0.9rem 0.75rem;
                }
                .table-charte tbody td {
                    padding: 0.9rem 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                    font-size: 0.85rem;
                }
                .table-charte tbody tr { transition: background 0.15s ease; }
                .table-charte tbody tr:hover { background: #f8fafc; }
                .table-charte tbody tr.row-late-charte { background: #fff7ed; }
                .table-charte tbody tr.row-late-charte:hover { background: #ffedd5; }

                .status-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    color: white; padding: 0.35rem 0.9rem;
                    border-radius: 50px; font-size: 0.7rem; font-weight: 600;
                    white-space: nowrap;
                }
                .badge-late-charte {
                    display: inline-flex; align-items: center; gap: 3px;
                    background: var(--warning); color: white;
                    padding: 0.15rem 0.5rem; border-radius: 50px;
                    font-size: 0.65rem; font-weight: 600;
                    margin-left: 0.5rem;
                    animation: pulse-soft 2s infinite;
                }
                @keyframes pulse-soft {
                    0%, 100% { transform: scale(1); }
                    50% { transform: scale(1.05); }
                }
                .employee-avatar-charte {
                    width: 42px; height: 42px;
                    border-radius: 12px;
                    background: linear-gradient(135deg, ${COLORS.primary}15, ${COLORS.secondary}15);
                    color: var(--primary);
                    display: flex; align-items: center; justify-content: center;
                    font-weight: 700; font-size: 0.85rem;
                    flex-shrink: 0;
                }

                /* ===== ACTION BTN ===== */
                .action-btn-charte {
                    width: 36px; height: 36px; border-radius: 10px;
                    display: inline-flex; align-items: center; justify-content: center;
                    background: white; border: 1.5px solid #e2e8f0;
                    color: #64748b; font-size: 1rem;
                    transition: all 0.2s ease;
                    cursor: pointer;
                }
                .action-btn-charte:hover {
                    border-color: var(--btn-color); color: var(--btn-color);
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(41, 90, 167, 0.15);
                }

                /* ===== EMPTY STATE ===== */
                .empty-state-charte { text-align: center; padding: 3rem 2rem; }
                .empty-state-icon-charte {
                    width: 72px; height: 72px;
                    background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 1rem; font-size: 1.8rem; color: #94a3b8;
                }

                /* ===== PAGINATION ===== */
                .pagination-wrapper-charte {
                    display: flex; justify-content: space-between; align-items: center;
                    padding: 1rem 1.5rem;
                    background: white;
                    border-radius: 12px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    flex-wrap: wrap; gap: 1rem;
                }
                .pagination-info-charte { font-size: 0.8rem; color: #64748b; }
                .pagination-charte { gap: 6px; }
                .pagination-charte .page-item .page-link {
                    border-radius: 10px;
                    border: 1.5px solid #e2e8f0;
                    color: #1e293b;
                    padding: 0.5rem 0.9rem;
                    font-weight: 500; font-size: 0.85rem;
                    transition: all 0.2s;
                }
                .pagination-charte .page-item.active .page-link {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border-color: var(--primary);
                    color: white;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                }
                .pagination-charte .page-item .page-link:hover {
                    background: #f1f5f9;
                    border-color: var(--secondary);
                    color: var(--primary);
                    transform: translateY(-1px);
                }

                /* ===== MODAL ===== */
                .modal-charte .modal-content {
                    border: none; border-radius: 20px; overflow: hidden;
                    box-shadow: 0 30px 80px rgba(0,0,0,0.15);
                }
                .modal-header-charte { padding: 1.25rem 1.5rem; border: none; }
                .modal-header-charte .btn-close { filter: brightness(0) invert(1); opacity: 0.8; }
                .modal-header-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(255,255,255,0.2);
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.5rem; color: white;
                }
                .summary-card-charte {
                    background: linear-gradient(135deg, #f8fafc, #ffffff);
                    padding: 1rem 1.25rem;
                    border-radius: 12px;
                    border: 1px solid #e2e8f0;
                }
                .employe-avatar-charte {
                    width: 52px; height: 52px; border-radius: 12px;
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    display: flex; align-items: center; justify-content: center;
                    color: white; font-weight: 700; font-size: 1.15rem;
                    flex-shrink: 0;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.25);
                }

                /* ===== BOUTONS ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-primary {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(41,90,167,0.4); color: white;
                }
                .btn-modern-danger {
                    background: linear-gradient(135deg, var(--danger), #dc2626);
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(239,68,68,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-danger:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(239,68,68,0.4); color: white;
                }
                .btn-modern-warning {
                    background: linear-gradient(135deg, var(--warning), #d97706);
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(245,158,11,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-warning:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(245,158,11,0.4); color: white;
                }
                .btn-modern-outline:disabled,
                .btn-modern-primary:disabled,
                .btn-modern-danger:disabled { opacity: 0.6; transform: none; }

                /* ===== ALERTES ===== */
                .alert-error-charte {
                    border: none; background: #fef2f2;
                    border-left: 4px solid var(--danger);
                    border-radius: 16px; padding: 1rem 1.5rem;
                    box-shadow: 0 8px 32px rgba(239,68,68,0.08);
                }
                .alert-error-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(239,68,68,0.1);
                    display: flex; align-items: center; justify-content: center;
                    color: var(--danger); font-size: 1.5rem;
                }

                /* ===== LOADING ===== */
                .loading-spinner-charte { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                .loading-ring-charte {
                    position: absolute; inset: 0; border-radius: 50%;
                    border: 4px solid #e2e8f0; border-top-color: var(--primary);
                    animation: spin 1s linear infinite;
                }
                .loading-icon-charte {
                    position: absolute; top: 50%; left: 50%;
                    transform: translate(-50%, -50%);
                    font-size: 28px; color: var(--primary);
                }

                /* ===== TOAST ===== */
                .toast-notification-fixed {
                    position: fixed; bottom: 24px; right: 24px;
                    z-index: 9999; animation: slideInRight 0.3s ease-out;
                }
                .toast-modern {
                    background: white; border-radius: 16px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    min-width: 320px; max-width: 400px; overflow: hidden;
                }
                .toast-header-custom {
                    padding: 12px 20px;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .toast-close-btn {
                    background: transparent; border: none;
                    color: white; cursor: pointer; opacity: 0.8;
                }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress { height: 3px; width: 100%; animation: shrink linear forwards; }

                /* ===== ANIMATIONS ===== */
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .pagination-wrapper-charte { flex-direction: column; align-items: stretch; }
                    .pagination-info-charte { text-align: center; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}